import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Card } from "../ui/Card";
import { Spinner } from "../ui/Spinner";
import { api } from "../../services/api";
import { FaCheckCircle, FaCircle, FaExclamationCircle } from "react-icons/fa";

interface ISetupProgressItem {
  key: string;
  label: string;
  completed: boolean;
  required: boolean;
  details?: string;
}

interface ISetupProgress {
  overallProgress: number;
  completedItems: number;
  totalItems: number;
  items: ISetupProgressItem[];
}

interface SetupProgressIndicatorProps {
  onOpenWizard?: () => void;
  compact?: boolean;
}

export const SetupProgressIndicator: React.FC<SetupProgressIndicatorProps> = ({
  onOpenWizard,
  compact = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [progress, setProgress] = useState<ISetupProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isWizardPage = location.pathname === "/admin/setup-wizard";

  const fetchProgress = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/store/setup-progress");
      setProgress(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.setupProgress.errors.fetch"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, t]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  if (loading) {
    return (
      <Card padding="md">
        <div className="flex items-center justify-center py-4">
          <Spinner size="md" />
        </div>
      </Card>
    );
  }

  if (error || !progress) {
    return (
      <Card padding="md">
        <div className="text-center text-red-600 py-4">
          {error || t("admin.setupProgress.errors.fetch")}
        </div>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card padding="sm" className="cursor-pointer hover:bg-gray-50" onClick={onOpenWizard}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <div className="relative w-12 h-12">
                <svg className="w-12 h-12 transform -rotate-90">
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    className="text-gray-200"
                  />
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 20}`}
                    strokeDashoffset={`${2 * Math.PI * 20 * (1 - progress.overallProgress / 100)}`}
                    className="text-blue-600 transition-all duration-300"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-gray-700">{progress.overallProgress}%</span>
                </div>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">
                {t("admin.setupProgress.title")}
              </div>
              <div className="text-xs text-gray-500">
                {progress.completedItems} / {progress.totalItems} {t("admin.setupProgress.completed")}
              </div>
            </div>
          </div>
          {onOpenWizard && !isWizardPage && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpenWizard();
              }}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {t("admin.setupProgress.openWizard")}
            </button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card padding="md">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("admin.setupProgress.title")}
          </h3>
          {onOpenWizard && (
            <button
              onClick={onOpenWizard}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {t("admin.setupProgress.openWizard")}
            </button>
          )}
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{t("admin.setupProgress.overallProgress")}</span>
            <span className="font-semibold text-gray-900">{progress.overallProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress.overallProgress}%` }}
            />
          </div>
        </div>

        {/* Progress Items */}
        <div className="space-y-2">
          {progress.items.map((item) => (
            <div
              key={item.key}
              className={`flex items-start gap-3 p-2 rounded ${
                item.completed ? "bg-green-50" : item.required ? "bg-red-50" : "bg-gray-50"
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {item.completed ? (
                  <FaCheckCircle className="text-green-600" />
                ) : item.required ? (
                  <FaExclamationCircle className="text-red-600" />
                ) : (
                  <FaCircle className="text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${item.completed ? "text-green-900" : item.required ? "text-red-900" : "text-gray-700"}`}>
                    {item.label}
                  </span>
                  {item.required && !item.completed && (
                    <span className="text-xs text-red-600 font-medium">
                      {t("admin.setupProgress.required")}
                    </span>
                  )}
                </div>
                {item.details && (
                  <div className="text-xs text-gray-600 mt-0.5">{item.details}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

